import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  ScreenLoader,
  SCREEN_LOADER_SIZE_LABELS,
  SCREEN_LOADER_SIZES,
  type ScreenLoaderSize,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ScreenLoaderShowcase.css'

const ScreenLoaderShowcase: React.FC = () => {
  const [size, setSize] = useState<ScreenLoaderSize>('md')
  const [previewOpen, setPreviewOpen] = useState(true)
  const [demoOpen, setDemoOpen] = useState(false)

  useEffect(() => {
    if (!demoOpen) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => setDemoOpen(false), 2000)
    return () => window.clearTimeout(timeoutId)
  }, [demoOpen])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'screen-loader-size',
            label: 'Size',
            control: (
              <select
                id="screen-loader-size"
                className="ui-field-control"
                value={size}
                onChange={(event) =>
                  setSize(event.target.value as ScreenLoaderSize)
                }
              >
                {SCREEN_LOADER_SIZES.map((option) => (
                  <option key={option} value={option}>
                    {SCREEN_LOADER_SIZE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'screen-loader-preview-open',
            label: 'Inline preview',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="screen-loader-preview-open"
                  type="checkbox"
                  checked={previewOpen}
                  onChange={(event) => setPreviewOpen(event.target.checked)}
                />
                <span>Visible</span>
              </label>
            ),
          },
        ],
      },
    ],
    [previewOpen, size],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Screen loader"
        description="Full-screen overlay with the Enamel tooth mark (Untitled design 1) — the same loader used when opening Patient Management and other modules."
        stageClassName="ui-preview__stage--wide screen-loader-showcase__stage-wrap"
      >
        <div className="screen-loader-showcase__sheet">
          <section className="ui-showcase-live">
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              {
                'Inline preview of the loader surface. Use "Show full-screen" to trigger the real portal overlay (auto-hides after 2s).'
              }
            </p>
            <div className="screen-loader-showcase__actions">
              <Button
                variant="primary"
                size="md"
                label="Show full-screen"
                onClick={() => setDemoOpen(true)}
              />
            </div>
            <div className="screen-loader-showcase__stage">
              <ScreenLoader open={previewOpen} size={size} inline />
              {!previewOpen ? (
                <p className="screen-loader-showcase__empty">
                  Inline preview hidden
                </p>
              ) : null}
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <ScreenLoader open={demoOpen} size={size} aria-label="Loading module" />

      <UiPropertiesPanel
        componentName="Screen loader"
        sections={propertySections}
      />
    </>
  )
}

export default ScreenLoaderShowcase
